import React, { useCallback, useEffect, useState } from "react";

import {
  AppBar,
  Avatar,
  Divider,
  FormControl,
  Grid,
  Hidden,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { TabletMacTwoTone, ExitToApp } from "@material-ui/icons";
import ApartmentIcon from "@material-ui/icons/Apartment";
import { isEmpty } from "lodash";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import { AppState } from "store";

import logoSmall from "assets/images/logo-lello.png";
import logoMedium from "assets/images/logo-neutro.png";
import { useLoadingDialog } from "hooks/backdrop";
import { useFeedback } from "hooks/feedback";
import {
  CondominiumDTO,
  CondominiumLayoutDTO,
} from "models/dto/CondominiumDTO";
import { createTabletRegisterCode } from "services/personnelDepartment.service";
import { getAuthStyles } from "services/user.service";
import {
  logout,
  isAuthenticated,
  isAuthenticatedFromGDP,
  SESSION_STORAGE_USER_FULLNAME,
} from "store/actions/authActions";
import {
  getExpectedRealizedChartData,
  getPunchCardDetailsChartData,
} from "store/actions/chartsActions";
import {
  changeCondominium,
  getCondominiums,
  getPeriods,
  SESSION_STORAGE_REFERENCE,
  SESSION_STORAGE_REFERENCE_NAME,
} from "store/actions/condominiumActions";
import { getEmployees, selectEmployee } from "store/actions/employeesActions";
import {
  getPeriodReferencePunchCard,
  getTodayOccurrencesStatus,
  getTodayReferencePunchCard,
} from "store/actions/punchCardActions";
import { AuthReducerState } from "store/reducers/authReducer";
import { CondominiumReducerData } from "store/reducers/condominiumReducer";
import { formatFirstAndLastName } from "utils/formatFirstAndLastName";

import AutoComplete from "../AutoComplete";
import CreateCondominiumDialog from "../CreateCondominiumDialog";
import CustomDialog from "../CustomDialog";

import useStyles from "./styles";

const HeaderContainer: React.FC = () => {
  const today = moment();
  const classes = useStyles();
  const [logo, setLogo] = useState<string>(logoMedium);
  const dispatch = useDispatch();
  const { addFeedback } = useFeedback();
  const { handleLoadingDialog } = useLoadingDialog();
  const [openTabletRegisterDialog, setOpenTabletRegisterDialog] = useState(
    false,
  );
  const [openNewCondominiumDialog, setOpenNewCondominiumDialog] = useState(
    false,
  );
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const {
    condominiumSelected,
    periods,
    condominiums,
    loading,
  }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const [
    tabletCondominiumSelected,
    setTabletCondominiumSelected,
  ] = useState<CondominiumDTO | null>(condominiumSelected);

  const { gdpTokenData }: AuthReducerState = useSelector(
    (state: AppState) => state.authReducer,
  );

  const getAuthStylesData = async (id: number) => {
    const { data } = await getAuthStyles(id);
    return data;
  };

  const handleChangeCondominium = useCallback(
    async (event: React.ChangeEvent<unknown>, value: CondominiumDTO) => {
      if (!value) {
        return;
      }

      const layoutDataArray: any = await getAuthStylesData(value.referencia);
      const layoutData: CondominiumLayoutDTO = layoutDataArray;
      const layout: CondominiumLayoutDTO = {
        corPrimaria: layoutData?.corPrimaria || "",
        corSecundaria: layoutData?.corSecundaria || "",
        caminhoLogo: layoutData?.caminhoLogo || "",
        flAgenciaLello: layoutData?.flAgenciaLello || "",
        codAgencia: layoutData?.codAgencia || "",
        nomeAgencia: layoutData?.nomeAgencia || "",
      };

      value.layout = layout;

      dispatch(changeCondominium(value));
    },
    [dispatch],
  );

  useEffect(() => {
    if (condominiumSelected) {
      const condominiumLogoPath = condominiumSelected?.layout?.caminhoLogo;
      setLogo(condominiumLogoPath || logoMedium);
    }
  }, [condominiumSelected]);

  useEffect(() => {
    if (!loading && !isEmpty(condominiums)) {
      handleChangeCondominium(
        {} as React.ChangeEvent<unknown>,
        condominiums[0],
      );
    }
  }, [condominiums, loading, handleChangeCondominium]);

  const getGreetingTime = useCallback((): string => {
    let greeting = null;
    const splitMorning = 6;
    const splitAfternoon = 12;
    const splitEvening = 17;
    const currentHour = parseFloat(today.format("HH"));

    if (currentHour >= splitAfternoon && currentHour <= splitEvening) {
      greeting = "Boa tarde,";
    } else if (currentHour >= splitEvening || currentHour < splitMorning) {
      greeting = "Boa noite,";
    } else {
      greeting = "Bom dia,";
    }

    return greeting;
  }, [today]);

  const handleChangeCondominiumTabletRegister = (
    event: React.ChangeEvent<unknown>,
    value: any,
  ) => {
    setTabletCondominiumSelected(value);
  };

  const handleNewRegisterCodeSubmit = async () => {
    if (tabletCondominiumSelected) {
      try {
        handleLoadingDialog(true);
        const { data } = await createTabletRegisterCode(
          tabletCondominiumSelected.referencia,
        );
        addFeedback({
          type: "success",
          description: `Código de Registro: ${data.codigo}`,
        });
        setOpenTabletRegisterDialog(false);
      } catch (error) {
        addFeedback({
          type: "error",
          description: "Ocorreu um erro ao gerar o código de registro.",
        });
      } finally {
        handleLoadingDialog(false);
      }
    }
  };

  useEffect(() => {
    if (!isAuthenticatedFromGDP()) {
      dispatch(getCondominiums());
    } else {
      const referenceName = sessionStorage.getItem(
        SESSION_STORAGE_REFERENCE_NAME,
      );
      const referenceCode = sessionStorage.getItem(SESSION_STORAGE_REFERENCE);

      if (referenceName && referenceCode) {
        dispatch(
          changeCondominium({
            cnpj: "",
            nome: referenceName,
            referencia: Number(referenceCode),
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (gdpTokenData) {
      dispatch(
        changeCondominium({
          cnpj: "",
          nome: gdpTokenData.nomeReferencia,
          referencia: gdpTokenData.referencia,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gdpTokenData]);

  useEffect(() => {
    dispatch(selectEmployee(null));
    if (condominiumSelected) {
      dispatch(getEmployees(condominiumSelected.referencia));
      dispatch(getPeriods(condominiumSelected.referencia));
      dispatch(getExpectedRealizedChartData(condominiumSelected.referencia));
      dispatch(getPunchCardDetailsChartData(condominiumSelected.referencia));
      dispatch(getTodayReferencePunchCard(condominiumSelected.referencia));
      dispatch(getTodayOccurrencesStatus(condominiumSelected.referencia));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominiumSelected]);

  useEffect(() => {
    if (condominiumSelected && periods.length > 0) {
      dispatch(
        getPeriodReferencePunchCard(
          moment(periods[0].inicioMesApuracao, "DD/MM/YYYY").format(
            "YYYY-MM-DD",
          ),
          moment(periods[0].fimMesApuracao, "DD/MM/YYYY").format("YYYY-MM-DD"),
          condominiumSelected?.referencia,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condominiumSelected, periods]);

  return (
    <>
      <CustomDialog
        open={openTabletRegisterDialog}
        handleClose={() => setOpenTabletRegisterDialog(false)}
        handleConfirm={handleNewRegisterCodeSubmit}
        maxWidth="xs"
        okButtonLabel="Gerar"
        disabledOkButton={!tabletCondominiumSelected}
        title="Gerador de código de registro"
        contentDividers
        keepMounted={false}
      >
        <Typography
          style={{ fontWeight: "bold" }}
          color="textPrimary"
          variant="body1"
        >
          Condomínio:
        </Typography>

        <AutoComplete
          value={condominiumSelected}
          loading={loading}
          options={condominiums.sort((a, b) =>
            a.referencia > b.referencia ? 1 : -1,
          )}
          getOptionLabel={(option: CondominiumDTO) =>
            `${option.referencia} - ${option.nome}`}
          onChange={handleChangeCondominiumTabletRegister}
          disabled={!!isAuthenticatedFromGDP && !isAuthenticated()}
        />
      </CustomDialog>

      <CreateCondominiumDialog
        open={openNewCondominiumDialog}
        handleClose={() => setOpenNewCondominiumDialog(false)}
      />

      <AppBar
        className={classes.headerContainer}
        position="static"
        elevation={0}
        color="default"
      >
        <Toolbar>
          <Hidden smDown>
            <img
              className={classes.logoMedium}
              src={logo}
              alt="Lello Digital"
            />
          </Hidden>
          <Hidden only={["md", "lg", "xl"]}>
            <img
              className={classes.logoSmall}
              src={logoSmall}
              alt="Lello Digital"
            />
          </Hidden>

          <Grid
            className={classes.headerContent}
            spacing={1}
            container
            wrap="nowrap"
            alignItems="center"
          >
            <Grid item xs="auto" sm={10} md={4}>
              <FormControl fullWidth>
                <AutoComplete
                  value={condominiumSelected}
                  loading={loading}
                  options={condominiums.sort((a, b) =>
                    a.referencia > b.referencia ? 1 : -1,
                  )}
                  getOptionLabel={(option: CondominiumDTO) =>
                    `${option.referencia} - ${option.nome}`}
                  onChange={handleChangeCondominium}
                  placeholder="Selecione um Condomínio"
                  disabled={!!isAuthenticatedFromGDP && !isAuthenticated()}
                />
              </FormControl>
            </Grid>

            <Divider
              className={classes.divider}
              orientation="vertical"
              variant="middle"
            />

            <Hidden smDown>
              <Grid item md={5}>
                <Typography color="textSecondary" variant="subtitle1">
                  {moment().format("dddd, LL")}
                </Typography>
              </Grid>
            </Hidden>

            <Hidden xsDown>
              {/* <SearchIcon
              className={classes.headerIcon}
              style={{ marginRight: '10px' }}
            /> */}

              {/* <Badge color="error" variant="dot" overlap="circle">
              <NotificationsIcon className={classes.headerIcon} />
            </Badge> */}

              <Divider
                className={classes.divider}
                orientation="vertical"
                variant="middle"
              />
            </Hidden>

            <Hidden smDown>
              <Grid item md={3}>
                <Typography
                  color="textPrimary"
                  style={{ fontWeight: 500 }}
                  variant="subtitle1"
                >
                  {getGreetingTime()}{" "}
                  {formatFirstAndLastName(
                    gdpTokenData?.nomeUsuario ||
                      sessionStorage.getItem(SESSION_STORAGE_USER_FULLNAME) ||
                      "",
                  )}
                </Typography>
              </Grid>
            </Hidden>
            <Grid item xs={1}>
              <Avatar className={classes.avatar} alt="Avatar" />
            </Grid>
            <Hidden smDown>
              {!gdpTokenData && (
                <>
                  <Tooltip title="Cadastrar condomínio" arrow>
                    <IconButton
                      onClick={() => setOpenNewCondominiumDialog(true)}
                    >
                      <ApartmentIcon fontSize="large" />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Gerar código de registro de tablet" arrow>
                    <IconButton
                      onClick={() => setOpenTabletRegisterDialog(true)}
                    >
                      <TabletMacTwoTone />
                    </IconButton>
                  </Tooltip>
                </>
              )}

              <Tooltip title="Sair do Sistema" arrow>
                <IconButton onClick={() => setOpenConfirmationDialog(true)}>
                  <ExitToApp />
                </IconButton>
              </Tooltip>
            </Hidden>
          </Grid>
        </Toolbar>
      </AppBar>

      <CustomDialog
        open={openConfirmationDialog}
        handleClose={() => setOpenConfirmationDialog(false)}
        handleConfirm={() => dispatch(logout(true))}
        title="Confirmação"
        okButtonLabel="Confirmar"
        maxWidth="xs"
      >
        <Grid container style={{ paddingBottom: "20px" }}>
          <Typography>Deseja realmente sair do sistema?</Typography>
        </Grid>
      </CustomDialog>
    </>
  );
};

export default HeaderContainer;
